import { Component } from '@theme/component';
import { fetchConfig, preloadImage, onAnimationEnd, yieldToMainThread, parseIntOrDefault } from '@theme/utilities';
import { cartPerformance } from '@theme/performance';
import { morph } from '@theme/morph';
import { CartLinesUpdateEvent, CartErrorEvent, ProductSelectEvent, StandardEvents } from '@shopify/events';
import { resolveVariantId } from '@theme/variant-resolution';

// Error message display duration - gives users time to read the message
const ERROR_MESSAGE_DISPLAY_DURATION = 10000;

// Button re-enable delay after error - prevents rapid repeat attempts
const ERROR_BUTTON_REENABLE_DELAY = 1000;

// Success message display duration for screen readers
const SUCCESS_MESSAGE_DISPLAY_DURATION = 5000;

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];

  /** @type {number[] | undefined} */
  #resetTimeouts = /** @type {number[]} */ ([]);

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('pointerenter', this.#preloadImage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#resetTimeouts) {
      this.#resetTimeouts.forEach(
        /** @param {number} timeoutId */
        (timeoutId) => clearTimeout(timeoutId)
      );
    }

    this.removeEventListener('pointerenter', this.#preloadImage);
  }

  /**
   * Disables the add to cart button.
   */
  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  /**
   * Enables the add to cart button.
   */
  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  /**
   * Handles the click event for the add to cart button.
   * @param {MouseEvent & {target: HTMLElement}} event - The click event.
   */
  handleClick(event) {
    const form = this.closest('form');
    if (!form?.checkValidity()) return;

    const productForm =
      /** @type {ProductFormComponent | null} */
      (this.closest('product-form-component'));

    const quantitySelector = productForm?.refs.quantitySelector;

    if (quantitySelector?.canAddToCart) {
      const validation = quantitySelector.canAddToCart();

      if (!validation.canAdd) {
        return;
      }
    }

    if (this.refs.addToCartButton.dataset.puppet !== 'true') {
      const animationEnabled = this.dataset.addToCartAnimation === 'true';

      if (animationEnabled && !event.target.closest('.quick-add-modal')) {
        this.#animateFlyToCart();
      }

      this.animateAddToCart();
    }
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;

    if (!image) return;

    preloadImage(image);
  };

  /**
   * Animates the fly to cart animation.
   */
  #animateFlyToCart() {
    const { addToCartButton } = this.refs;
    const cartIcon = document.querySelector('.header-actions__cart-icon');
    const image = this.dataset.productVariantMedia;

    if (!cartIcon || !addToCartButton || !image) return;

    const flyToCartElement =
      /** @type {FlyToCart} */
      (document.createElement('fly-to-cart'));

    const flyToCartClass = addToCartButton.classList.contains('quick-add__button')
      ? 'fly-to-cart--quick'
      : 'fly-to-cart--main';

    flyToCartElement.classList.add(flyToCartClass);
    flyToCartElement.style.setProperty('background-image', `url(${image})`);
    flyToCartElement.style.setProperty('--start-opacity', '0');
    flyToCartElement.source = addToCartButton;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }

  /**
   * Animates the add to cart button.
   */
  animateAddToCart = async function () {
    const { addToCartButton } = this.refs;

    if (!this.#resetTimeouts) {
      this.#resetTimeouts = [];
    }

    this.#resetTimeouts.forEach(
      /** @param {number} timeoutId */
      (timeoutId) => clearTimeout(timeoutId)
    );

    this.#resetTimeouts = [];

    if (addToCartButton.dataset.added !== 'true') {
      addToCartButton.dataset.added = 'true';
    }

    await yieldToMainThread();
    await onAnimationEnd(addToCartButton);

    const timeoutId = setTimeout(() => {
      addToCartButton.removeAttribute('data-added');

      const index = this.#resetTimeouts.indexOf(timeoutId);

      if (index > -1) {
        this.#resetTimeouts.splice(index, 1);
      }
    }, 800);

    this.#resetTimeouts.push(timeoutId);
  };
}

if (!customElements.get('add-to-cart-component')) {
  customElements.define('add-to-cart-component', AddToCartComponent);
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {{items: Array<{quantity: number, variant_id: number}>}} Cart
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId
 * @property {AddToCartComponent | undefined} addToCartButtonContainer
 * @property {HTMLElement | undefined} addToCartTextError
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer
 * @property {HTMLElement} liveRegion
 * @property {HTMLElement | undefined} quantityLabelCartCount
 * @property {HTMLElement | undefined} quantityRules
 * @property {HTMLElement | undefined} productFormButtons
 * @property {HTMLElement | undefined} volumePricing
 * @property {any | undefined} quantitySelector
 * @property {HTMLElement | undefined} quantitySelectorWrapper
 * @property {HTMLElement | undefined} quantityLabel
 * @property {HTMLElement | undefined} pricePerItem
 *
 * @typedef {object} QueuedAddToCartItem
 * @property {number} quantity
 * @property {number} generation
 * @property {string | null} intendedVariantId
 * @property {Promise<unknown> | null} pendingVariantChange
 * @property {string | null} variantResolutionUrl
 *
 * @typedef {object} QuantityConstraints
 * @property {string} min
 * @property {string | null} max
 * @property {string} step
 * @property {string | null} cartQuantity
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];

  #abortController = new AbortController();

  /** @type {number | undefined} */
  #timeout;

  /** @type {boolean} */
  #variantChangeInProgress = false;

  /** @type {number} */
  #variantChangeGeneration = 0;

  /**
   * Prevents multiple cart requests from being submitted concurrently.
   * @type {boolean}
   */
  #addToCartInProgress = false;

  /**
   * Prevents one user interaction from being queued multiple times
   * while a variant selection is resolving.
   * @type {boolean}
   */
  #queuedAddPending = false;

  /**
   * Adds queued while a variant change is in flight.
   * @type {QueuedAddToCartItem[]}
   */
  #addToCartQueue = [];

  /**
   * The in-flight variant-change section fetch promise.
   * @type {Promise<unknown> | null}
   */
  #pendingVariantChange = null;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog, product-card');

    target?.addEventListener(
      StandardEvents.productSelect,
      this.#onProductSelect,
      { signal }
    );

    document.addEventListener(
      StandardEvents.cartLinesUpdate,
      this.#onCartUpdate,
      { signal }
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
  }

  #getVariantIdInput() {
    return (
      /** @type {HTMLInputElement | null} */
      (this.querySelector('input[name="id"]'))
    )?.value;
  }

  async #refreshCart() {
    const cartItemsComponent =
      /** @type {import('@theme/component-cart-items').CartItemsComponent | null} */
      (document.querySelector('cart-items-component'));

    if (cartItemsComponent) {
      await customElements.whenDefined('cart-items-component');

      return cartItemsComponent.fetchCartData();
    }

    return fetch(`${Theme.routes.cart_url}.json`, {
      headers: {
        Accept: 'application/json',
      },
      credentials: 'same-origin',
    }).then((response) => {
      if (!response.ok) {
        throw new Error(`Failed to fetch cart: ${response.status}`);
      }

      return response.json();
    });
  }

  /**
   * @param {Cart} cart
   */
  #updateCartQuantity(cart) {
    const variantIdInput = this.#getVariantIdInput();

    if (!variantIdInput) return;

    const cartItem = cart.items.find(
      /** @param {any} item */
      (item) => item.variant_id.toString() === variantIdInput.toString()
    );

    const cartQty = cartItem ? cartItem.quantity : 0;

    const quantitySelector =
      /** @type {import('@theme/component-cart-quantity-selector').CartQuantitySelectorComponent | null} */
      (this.querySelector('quantity-selector-component'));

    if (quantitySelector?.setCartQuantity) {
      quantitySelector.setCartQuantity(cartQty);
    }

    this.#updateQuantityLabel(cartQty);
  }

  /**
   * @param {CartLinesUpdateEvent} event
   */
  #onCartUpdate = async (event) => {
    if (!this.#getVariantIdInput()) return;

    event.promise
      ?.then(({ detail }) => {
        if (
          detail?.sourceId === this.id ||
          detail?.source === 'product-form-component'
        ) {
          return;
        }

        if (detail?.items) {
          this.#updateCartQuantity(
            /** @type {Cart} */
            ({ items: detail.items })
          );
        } else {
          this.#refreshCart().then((cart) =>
            this.#updateCartQuantity(cart)
          );
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          console.warn(
            '[product-form] Event promise rejected:',
            error
          );
        }
      });
  };

  /**
   * @param {Event} event
   */
  handleSubmit(event) {
    event.preventDefault();

    /**
     * Prevent a second submit while the current cart request
     * is still being processed.
     */
    if (this.#addToCartInProgress) {
      return;
    }

    if (this.#variantChangeInProgress) {
      /**
       * A single click can sometimes generate more than one
       * submit event while variant resolution is happening.
       *
       * Only queue one add request.
       */
      if (this.#queuedAddPending) {
        return;
      }

      this.#queuedAddPending = true;

      this.#addToCartQueue.push(
        this.#createQueuedAddToCartItem()
      );

      this.refs.addToCartButtonContainer?.animateAddToCart?.();

      return;
    }

    this.#processAddToCart(
      undefined,
      undefined,
      event
    );
  }

  /** @returns {number} */
  #getQuantity() {
    return (
      Number(this.refs.quantitySelector?.getValue?.()) ||
      Number(this.dataset.quantityDefault) ||
      1
    );
  }

  /** @returns {QueuedAddToCartItem} */
  #createQueuedAddToCartItem() {
    const picker = this.#getVariantPicker();
    const selectedOption = picker?.selectedOption;

    return {
      quantity: this.#getQuantity(),
      generation: this.#variantChangeGeneration,
      intendedVariantId:
        selectedOption?.dataset.variantId ?? null,
      pendingVariantChange:
        this.#pendingVariantChange,
      variantResolutionUrl:
        picker && selectedOption
          ? picker.buildRequestUrl(selectedOption)
          : null,
    };
  }

  /**
   * @param {string} [overrideVariantId]
   * @param {number} [overrideQuantity]
   * @param {Event} [event]
   */
  #processAddToCart(
    overrideVariantId,
    overrideQuantity,
    event
  ) {
    const { addToCartTextError } = this.refs;

    if (this.#timeout) {
      clearTimeout(this.#timeout);
    }

    const allAddToCartContainers =
      /** @type {NodeListOf<AddToCartComponent>} */
      (
        this.querySelectorAll(
          'add-to-cart-component'
        )
      );

    if (!overrideVariantId) {
      const anyButtonDisabled = Array.from(
        allAddToCartContainers
      ).some(
        (container) =>
          container.refs.addToCartButton?.disabled
      );

      if (anyButtonDisabled) return;
    }

    const form = this.querySelector('form');

    if (!form) {
      throw new Error(
        'Product form element missing'
      );
    }

    if (
      !overrideVariantId &&
      this.refs.quantitySelector?.canAddToCart
    ) {
      const validation =
        this.refs.quantitySelector.canAddToCart();

      if (!validation.canAdd) {
        for (const container of allAddToCartContainers) {
          container.disable();
        }

        const errorTemplate =
          this.dataset.quantityErrorMax || '';

        const errorMessage =
          errorTemplate.replace(
            '{{ maximum }}',
            validation.maxQuantity?.toString() || ''
          );

        if (addToCartTextError) {
          addToCartTextError.classList.remove(
            'hidden'
          );

          const textNode =
            addToCartTextError.childNodes[2];

          if (textNode) {
            textNode.textContent =
              errorMessage;
          } else {
            const newTextNode =
              document.createTextNode(
                errorMessage
              );

            addToCartTextError.appendChild(
              newTextNode
            );
          }

          this.#setLiveRegionText(
            errorMessage
          );

          if (this.#timeout) {
            clearTimeout(this.#timeout);
          }

          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;

            addToCartTextError.classList.add(
              'hidden'
            );

            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);
        }

        setTimeout(() => {
          for (
            const container of allAddToCartContainers
          ) {
            container.enable();
          }
        }, ERROR_BUTTON_REENABLE_DELAY);

        return;
      }
    }

    /**
     * Lock the request only after all validation has passed.
     */
    if (this.#addToCartInProgress) {
      return;
    }

    this.#addToCartInProgress = true;

    const formData = new FormData(form);

    if (overrideVariantId) {
      formData.set(
        'id',
        overrideVariantId
      );
    }

    if (overrideQuantity !== undefined) {
      formData.set(
        'quantity',
        overrideQuantity.toString()
      );
    }

    const cartItemsComponents =
      document.querySelectorAll(
        'cart-items-component'
      );

    const cartItemComponentsSectionIds = [];

    cartItemsComponents.forEach((item) => {
      if (
        item instanceof HTMLElement &&
        item.dataset.sectionId
      ) {
        cartItemComponentsSectionIds.push(
          item.dataset.sectionId
        );
      }
    });

    /**
     * Append sections once, after all section IDs
     * have been collected.
     */
    if (cartItemComponentsSectionIds.length) {
      formData.set(
        'sections',
        cartItemComponentsSectionIds.join(',')
      );
    }

    const itemCount =
      Number(formData.get('quantity')) ||
      Number(this.dataset.quantityDefault) ||
      1;

    const deferredEventPromise =
      CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [
          {
            merchandiseId:
              /** @type {string} */
              (formData.get('id')),
            quantity: itemCount,
          },
        ],
        promise:
          deferredEventPromise.promise,
      })
    );

    const fetchCfg = fetchConfig(
      'javascript',
      {
        body: formData,
      }
    );

    fetch(
      Theme.routes.cart_add_url,
      {
        ...fetchCfg,
        headers: {
          ...fetchCfg.headers,
          Accept: 'text/html',
        },
      }
    )
      .then((response) =>
        response.json()
      )
      .then(async (response) => {
        if (response.status) {
          this.dispatchEvent(
            new CartErrorEvent({
              error:
                response.message ||
                'Add to cart failed',
              code: 'INVALID',
              detail: {
                description:
                  response.description,
                errors: response.errors,
              },
            })
          );

          this.#refreshCart()
            .then((ajaxCart) =>
              deferredEventPromise.resolve({
                cart:
                  CartLinesUpdateEvent.createCartFromAjaxResponse(
                    ajaxCart
                  ),
                detail: {
                  didError: true,
                  items: ajaxCart.items,
                  source:
                    'product-form-component',
                  sourceId:
                    this.id.toString(),
                  itemCount,
                  productId:
                    this.dataset.productId,
                },
              })
            )
            .catch(
              deferredEventPromise.reject
            );

          if (!addToCartTextError) {
            return;
          }

          addToCartTextError.classList.remove(
            'hidden'
          );

          const textNode =
            addToCartTextError.childNodes[2];

          if (textNode) {
            textNode.textContent =
              response.message;
          } else {
            const newTextNode =
              document.createTextNode(
                response.message
              );

            addToCartTextError.appendChild(
              newTextNode
            );
          }

          this.#setLiveRegionText(
            response.message
          );

          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) {
              return;
            }

            addToCartTextError.classList.add(
              'hidden'
            );

            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);

          return;
        }

        const id = formData.get('id');

        if (addToCartTextError) {
          addToCartTextError.classList.add(
            'hidden'
          );

          addToCartTextError.removeAttribute(
            'aria-live'
          );
        }

        if (!id) {
          throw new Error(
            'Form ID is required'
          );
        }

        const anyAddToCartButton =
          allAddToCartContainers[0]
            ?.refs.addToCartButton;

        if (anyAddToCartButton) {
          const addedTextElement =
            anyAddToCartButton.querySelector(
              '.add-to-cart-text--added'
            );

          const addedText =
            addedTextElement?.textContent?.trim() ||
            Theme.translations.added;

          this.#setLiveRegionText(
            addedText
          );

          setTimeout(() => {
            this.#clearLiveRegionText();
          }, SUCCESS_MESSAGE_DISPLAY_DURATION);
        }

        await this.#refreshCart()
          .then((ajaxCart) => {
            deferredEventPromise.resolve({
              cart:
                CartLinesUpdateEvent.createCartFromAjaxResponse(
                  ajaxCart
                ),
              detail: {
                items: ajaxCart.items,
                source:
                  'product-form-component',
                sourceId:
                  this.id.toString(),
                itemCount,
                productId:
                  this.dataset.productId,
                sections:
                  response.sections,
                didError: false,
              },
            });

            if (
              this.#getVariantIdInput()
            ) {
              this.#updateCartQuantity(
                ajaxCart
              );
            }

            return ajaxCart;
          })
          .catch(
            deferredEventPromise.reject
          );
      })
      .catch((error) => {
        console.error(error);

        deferredEventPromise.reject(
          error
        );

        this.dispatchEvent(
          new CartErrorEvent({
            error:
              error?.message ||
              'Network error during add to cart',
            code: 'SERVICE_UNAVAILABLE',
          })
        );
      })
      .finally(() => {
        /**
         * Release duplicate-submission lock.
         */
        this.#addToCartInProgress = false;

        if (event) {
          cartPerformance.measureFromEvent(
            'add:user-action',
            event
          );
        }
      });
  }

  /**
   * @param {Array<{variantId: string, quantity: number}>} items
   */
  #processBatchAddToCart(items) {
    if (items.length === 0) {
      this.#queuedAddPending = false;
      return;
    }

    /**
     * Prevent batch cart requests from overlapping
     * another add request.
     */
    if (this.#addToCartInProgress) {
      this.#queuedAddPending = false;
      return;
    }

    this.#addToCartInProgress = true;
    this.#queuedAddPending = false;

    const { addToCartTextError } =
      this.refs;

    if (this.#timeout) {
      clearTimeout(this.#timeout);
    }

    const cartItemsComponents =
      document.querySelectorAll(
        'cart-items-component'
      );

    const cartItemComponentsSectionIds =
      [];

    for (const item of cartItemsComponents) {
      if (
        item instanceof HTMLElement &&
        item.dataset.sectionId
      ) {
        cartItemComponentsSectionIds.push(
          item.dataset.sectionId
        );
      }
    }

    const totalQuantity =
      items.reduce(
        (sum, item) =>
          sum + item.quantity,
        0
      );

    const deferredEventPromise =
      CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((item) => ({
          merchandiseId:
            item.variantId,
          quantity:
            item.quantity,
        })),
        promise:
          deferredEventPromise.promise,
      })
    );

    const payload = {
      items: items.map((item) => ({
        id: Number(item.variantId),
        quantity: item.quantity,
      })),
      sections:
        cartItemComponentsSectionIds.join(
          ','
        ),
    };

    fetch(
      Theme.routes.cart_add_url,
      {
        method: 'POST',
        headers: {
          'Content-Type':
            'application/json',
          Accept:
            'application/json',
        },
        body: JSON.stringify(payload),
      }
    )
      .then((response) =>
        response.json()
      )
      .then(async (response) => {
        if (response.status) {
          this.dispatchEvent(
            new CartErrorEvent({
              error:
                response.message ||
                'Add to cart failed',
              code: 'INVALID',
              detail: {
                description:
                  response.description,
                errors:
                  response.errors,
              },
            })
          );

          this.#refreshCart()
            .then((ajaxCart) =>
              deferredEventPromise.resolve({
                cart:
                  CartLinesUpdateEvent.createCartFromAjaxResponse(
                    ajaxCart
                  ),
                detail: {
                  didError: true,
                  items:
                    ajaxCart.items,
                  source:
                    'product-form-component',
                  sourceId:
                    this.id.toString(),
                  itemCount:
                    totalQuantity,
                  productId:
                    this.dataset.productId,
                },
              })
            )
            .catch(
              deferredEventPromise.reject
            );

          if (!addToCartTextError) {
            return;
          }

          addToCartTextError.classList.remove(
            'hidden'
          );

          const textNode =
            addToCartTextError.childNodes[2];

          if (textNode) {
            textNode.textContent =
              response.message;
          } else {
            addToCartTextError.appendChild(
              document.createTextNode(
                response.message
              )
            );
          }

          this.#setLiveRegionText(
            response.message
          );

          this.#timeout = setTimeout(() => {
            addToCartTextError.classList.add(
              'hidden'
            );

            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);

          return;
        }

        if (addToCartTextError) {
          addToCartTextError.classList.add(
            'hidden'
          );

          addToCartTextError.removeAttribute(
            'aria-live'
          );
        }

        const allAddToCartContainers =
          /** @type {NodeListOf<AddToCartComponent>} */
          (
            this.querySelectorAll(
              'add-to-cart-component'
            )
          );

        const anyAddToCartButton =
          allAddToCartContainers[0]
            ?.refs.addToCartButton;

        if (anyAddToCartButton) {
          const addedTextElement =
            anyAddToCartButton.querySelector(
              '.add-to-cart-text--added'
            );

          const addedText =
            addedTextElement?.textContent?.trim() ||
            Theme.translations.added;

          this.#setLiveRegionText(
            addedText
          );

          setTimeout(
            () =>
              this.#clearLiveRegionText(),
            SUCCESS_MESSAGE_DISPLAY_DURATION
          );
        }

        const cart =
          await this.#refreshCart();

        deferredEventPromise.resolve({
          cart:
            CartLinesUpdateEvent.createCartFromAjaxResponse(
              cart
            ),
          detail: {
            items: cart.items,
            source:
              'product-form-component',
            sourceId:
              this.id.toString(),
            itemCount:
              totalQuantity,
            productId:
              this.dataset.productId,
            sections:
              response.sections,
            didError: false,
          },
        });

        this.#updateCartQuantity(
          cart
        );
      })
      .catch((error) => {
        console.error(error);

        deferredEventPromise.reject(
          error
        );

        this.dispatchEvent(
          new CartErrorEvent({
            error:
              error?.message ||
              'Network error during add to cart',
            code: 'SERVICE_UNAVAILABLE',
          })
        );
      })
      .finally(() => {
        this.#addToCartInProgress = false;
        this.#queuedAddPending = false;
      });
  }

  /**
   * @param {number} cartQty
   */
  #updateQuantityLabel(cartQty) {
    const quantityLabel =
      this.refs.quantityLabelCartCount;

    if (quantityLabel) {
      const inCartText =
        quantityLabel.textContent?.match(
          /\((\d+)\s+(.+)\)/
        );

      if (
        inCartText &&
        inCartText[2]
      ) {
        quantityLabel.textContent =
          `(${cartQty} ${inCartText[2]})`;
      }

      quantityLabel.classList.toggle(
        'hidden',
        cartQty === 0
      );
    }
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    this.refs.liveRegion.textContent =
      text;
  }

  #clearLiveRegionText() {
    this.refs.liveRegion.textContent =
      '';
  }

  /**
   * @param {Element | null | undefined} currentElement
   * @param {Element | null | undefined} newElement
   * @param {Element | null} [insertReferenceElement]
   */
  #morphOrUpdateElement(
    currentElement,
    newElement,
    insertReferenceElement = null
  ) {
    if (
      currentElement &&
      newElement
    ) {
      morph(
        currentElement,
        newElement
      );
    } else if (
      currentElement &&
      !newElement
    ) {
      currentElement.remove();
    } else if (
      !currentElement &&
      newElement &&
      insertReferenceElement
    ) {
      insertReferenceElement.insertAdjacentElement(
        'beforebegin',
        /** @type {Element} */
        (newElement.cloneNode(true))
      );
    }
  }

  /**
   * @param {ProductSelectEvent} event
   */
  #onProductSelect = async (event) => {
    const sourceCard =
      /** @type {Element | null} */
      (event.target)?.closest(
        'product-card'
      );

    if (
      sourceCard &&
      !sourceCard.contains(this)
    ) {
      return;
    }

    const generation =
      ++this.#variantChangeGeneration;

    this.#variantChangeInProgress =
      true;

    this.#pendingVariantChange =
      event.promise;

    try {
      const { detail } =
        await event.promise;

      if (!detail?.html) return;

      const {
        html,
        newProduct,
        productId,
        resource,
      } = detail;

      if (newProduct) {
        this.dataset.productId =
          newProduct.id;
      } else if (
        productId &&
        productId !==
          this.dataset.productId
      ) {
        return;
      }

      const { variantId } =
        this.refs;

      variantId.value =
        resource?.id ?? '';

      const {
        addToCartButtonContainer:
          currentAddToCartButtonContainer,
        acceleratedCheckoutButtonContainer,
      } = this.refs;

      const currentAddToCartButton =
        currentAddToCartButtonContainer
          ?.refs.addToCartButton;

      if (
        !currentAddToCartButtonContainer ||
        (
          !currentAddToCartButton &&
          !acceleratedCheckoutButtonContainer
        )
      ) {
        return;
      }

      if (
        resource == null ||
        resource.available == false
      ) {
        currentAddToCartButtonContainer.disable();
      } else {
        currentAddToCartButtonContainer.enable();
      }

      const newAddToCartButton =
        html.querySelector(
          'product-form-component [ref="addToCartButton"]'
        );

      if (
        newAddToCartButton &&
        currentAddToCartButton
      ) {
        morph(
          currentAddToCartButton,
          newAddToCartButton
        );
      }

      if (
        acceleratedCheckoutButtonContainer
      ) {
        if (
          resource == null ||
          resource.available == false
        ) {
          acceleratedCheckoutButtonContainer.setAttribute(
            'hidden',
            'true'
          );
        } else {
          acceleratedCheckoutButtonContainer.removeAttribute(
            'hidden'
          );
        }
      }

      if (resource) {
        const productVariantMedia =
          resource.featured_media
            ?.preview_image?.src;

        if (productVariantMedia) {
          this.refs.addToCartButtonContainer?.setAttribute(
            'data-product-variant-media',
            productVariantMedia +
              '&width=100'
          );
        }
      }

      const {
        quantityRules,
        pricePerItem,
        quantitySelector,
        productFormButtons,
        quantityLabel,
        quantitySelectorWrapper,
      } = this.refs;

      const newQuantityInput =
        /** @type {HTMLInputElement | null} */
        (
          html.querySelector(
            'quantity-selector-component input[ref="quantityInput"]'
          )
        );

      if (
        quantitySelector
          ?.updateConstraints &&
        newQuantityInput
      ) {
        quantitySelector.updateConstraints(
          newQuantityInput.min,
          newQuantityInput.max || null,
          newQuantityInput.step
        );

        this.dataset.quantityDefault =
          newQuantityInput.min ||
          '1';
      }

      const newQuantityRules =
        html.querySelector(
          '.quantity-rules'
        );

      const isQuantityRulesChanging =
        !!quantityRules !==
        !!newQuantityRules;

      const newPricePerItem =
        html.querySelector(
          'price-per-item'
        );

      const isPricePerItemChanging =
        !!pricePerItem !==
        !!newPricePerItem;

      if (
        (
          isQuantityRulesChanging ||
          isPricePerItemChanging
        ) &&
        quantitySelector
      ) {
        const currentQuantityValue =
          quantitySelector.getValue?.();

        const newProductFormButtons =
          html.querySelector(
            '.product-form-buttons'
          );

        if (
          productFormButtons &&
          newProductFormButtons
        ) {
          morph(
            productFormButtons,
            newProductFormButtons
          );

          const newQuantityInputElement =
            /** @type {HTMLInputElement | null} */
            (
              html.querySelector(
                'quantity-selector-component input[ref="quantityInput"]'
              )
            );

          if (
            this.refs.quantitySelector
              ?.updateConstraints &&
            newQuantityInputElement &&
            currentQuantityValue
          ) {
            this.refs.quantitySelector.setValue(
              currentQuantityValue
            );

            this.refs.quantitySelector.updateConstraints(
              newQuantityInputElement.min,
              newQuantityInputElement.max ||
                null,
              newQuantityInputElement.step
            );

            this.dataset.quantityDefault =
              newQuantityInputElement.min ||
              '1';
          }
        }
      } else {
        const morphTargets =
          /** @type {Array<[string, HTMLElement | undefined, HTMLElement | undefined]>} */
          ([
            [
              '.quantity-label',
              quantityLabel,
              quantitySelector,
            ],
            [
              '.quantity-rules',
              quantityRules,
              this.refs
                .productFormButtons,
            ],
            [
              'price-per-item',
              pricePerItem,
              quantitySelectorWrapper,
            ],
          ]);

        for (
          const [
            selector,
            currentElement,
            fallback,
          ] of morphTargets
        ) {
          this.#morphOrUpdateElement(
            currentElement,
            html.querySelector(
              selector
            ),
            fallback
          );
        }
      }

      const currentVolumePricing =
        this.refs.volumePricing;

      const newVolumePricing =
        html.querySelector(
          'volume-pricing'
        );

      this.#morphOrUpdateElement(
        currentVolumePricing,
        newVolumePricing,
        this.refs.productFormButtons
      );

      const hasB2BFeatures =
        quantityRules ||
        newQuantityRules ||
        pricePerItem ||
        newPricePerItem ||
        currentVolumePricing ||
        newVolumePricing;

      if (!hasB2BFeatures) {
        return;
      }

      this.#refreshCart().then(
        (cart) =>
          this.#updateCartQuantity(
            cart
          )
      );
    } finally {
      if (
        generation ===
        this.#variantChangeGeneration
      ) {
        this.#variantChangeInProgress =
          false;

        await this.#drainAddToCartQueue();
      }
    }
  };

  /**
   * Drains the add-to-cart queue.
   */
  async #drainAddToCartQueue() {
    if (
      this.#addToCartQueue.length ===
      0
    ) {
      this.#queuedAddPending = false;
      return;
    }

    const queuedItems = [
      ...this.#addToCartQueue,
    ];

    this.#addToCartQueue = [];

    /**
     * Use a Map to ensure the same variant
     * cannot appear more than once in the batch.
     */
    const resolvedItemsByVariant =
      new Map();

    for (
      const item of queuedItems
    ) {
      const resolvedItem =
        await this.#resolveQueuedAddToCartItem(
          item
        );

      if (!resolvedItem) {
        continue;
      }

      if (
        !resolvedItemsByVariant.has(
          resolvedItem.variantId
        )
      ) {
        resolvedItemsByVariant.set(
          resolvedItem.variantId,
          resolvedItem
        );
      }
    }

    const resolvedItems =
      Array.from(
        resolvedItemsByVariant.values()
      );

    this.#processBatchAddToCart(
      resolvedItems
    );
  }

  /**
   * @param {QueuedAddToCartItem} item
   * @returns {Promise<{variantId: string, quantity: number} | null>}
   */
  async #resolveQueuedAddToCartItem(
    item
  ) {
    const {
      variantId,
      quantityConstraints,
    } =
      await this.#resolveQueuedVariant(
        item
      );

    if (!variantId) {
      return null;
    }

    return {
      variantId,
      quantity:
        this.#normalizeQueuedQuantity(
          item.quantity,
          quantityConstraints
        ),
    };
  }

  /**
   * @param {QueuedAddToCartItem} item
   * @returns {Promise<{variantId: string | null, quantityConstraints: QuantityConstraints | null}>}
   */
  async #resolveQueuedVariant(item) {
    /** @type {string | null} */
    let resolvedVariantId = null;

    /** @type {boolean | undefined} */
    let available;

    /** @type {QuantityConstraints | null} */
    let quantityConstraints = null;

    if (
      item.pendingVariantChange
    ) {
      try {
        const result =
          /** @type {{detail?: {resource?: any, html?: Document | Element}}} */
          (
            await item.pendingVariantChange
          );

        const resource =
          result?.detail?.resource;

        quantityConstraints =
          this.#getQuantityConstraintsFromHtml(
            result?.detail?.html
          );

        if (
          resource === null
        ) {
          available = false;
        } else if (resource) {
          resolvedVariantId =
            resource.id != null
              ? String(resource.id)
              : null;

          available =
            resource.available !==
            false;
        }
      } catch {
        const resolvedVariant =
          await this.#resolveVariantFromUrl(
            item.variantResolutionUrl
          ).catch(() => ({
            variantId: null,
            available: false,
            quantityConstraints: null,
          }));

        resolvedVariantId =
          resolvedVariant.variantId;

        available =
          resolvedVariant.available;

        quantityConstraints =
          resolvedVariant.quantityConstraints;
      }
    }

    const isLatestGeneration =
      item.generation ===
      this.#variantChangeGeneration;

    const variantId =
      resolveVariantId({
        resolvedVariantId,
        intendedVariantId:
          item.intendedVariantId,
        hiddenInputValue:
          isLatestGeneration
            ? this.#getVariantIdInput() ??
              null
            : null,
        available:
          available ??
          (
            isLatestGeneration
              ? !this.#isAddToCartDisabled()
              : undefined
          ),
      });

    return {
      variantId,
      quantityConstraints,
    };
  }

  /**
   * @param {number} quantity
   * @param {QuantityConstraints | null} quantityConstraints
   */
  #normalizeQueuedQuantity(
    quantity,
    quantityConstraints
  ) {
    if (!quantityConstraints) {
      return quantity;
    }

    const min = parseIntOrDefault(
      quantityConstraints.min,
      1
    );

    const max = parseIntOrDefault(
      quantityConstraints.max,
      null
    );

    const step = parseIntOrDefault(
      quantityConstraints.step,
      1
    );

    const cartQuantity =
      parseIntOrDefault(
        quantityConstraints.cartQuantity,
        0
      );

    const effectiveMax =
      max === null
        ? null
        : Math.max(
            max - cartQuantity,
            min
          );

    let normalizedQuantity =
      quantity;

    if (
      (quantity - min) %
        step !==
      0
    ) {
      normalizedQuantity =
        min +
        Math.floor(
          (quantity - min) /
            step
        ) *
          step;
    }

    return Math.max(
      min,
      Math.min(
        effectiveMax ?? Infinity,
        normalizedQuantity
      )
    );
  }

  /**
   * @param {Document | Element | null | undefined} html
   * @returns {QuantityConstraints | null}
   */
  #getQuantityConstraintsFromHtml(
    html
  ) {
    const quantityInput =
      /** @type {HTMLInputElement | null} */
      (
        html?.querySelector?.(
          'quantity-selector-component input[ref="quantityInput"]'
        ) ?? null
      );

    if (!quantityInput) {
      return null;
    }

    return {
      min:
        quantityInput.min,
      max:
        quantityInput.max ||
        null,
      step:
        quantityInput.step,
      cartQuantity:
        quantityInput.getAttribute(
          'data-cart-quantity'
        ),
    };
  }

  /**
   * @param {string | null} variantResolutionUrl
   * @returns {Promise<{variantId: string | null, available: boolean | undefined, quantityConstraints: QuantityConstraints | null}>}
   */
  async #resolveVariantFromUrl(
    variantResolutionUrl
  ) {
    if (!variantResolutionUrl) {
      return {
        variantId: null,
        available: undefined,
        quantityConstraints: null,
      };
    }

    const response =
      await fetch(
        variantResolutionUrl,
        {
          credentials:
            'same-origin',
        }
      );

    if (!response.ok) {
      return {
        variantId: null,
        available: false,
        quantityConstraints: null,
      };
    }

    const html =
      new DOMParser().parseFromString(
        await response.text(),
        'text/html'
      );

    const quantityConstraints =
      this.#getQuantityConstraintsFromHtml(
        html
      );

    const textContent =
      html.querySelector(
        'variant-picker script[type="application/json"]'
      )?.textContent;

    if (!textContent) {
      return {
        variantId: null,
        available: false,
        quantityConstraints,
      };
    }

    const resource =
      JSON.parse(textContent);

    if (
      !resource ||
      typeof resource !== 'object'
    ) {
      return {
        variantId: null,
        available: false,
        quantityConstraints,
      };
    }

    return {
      variantId:
        resource.id != null
          ? String(resource.id)
          : null,
      available:
        resource.available !==
        false,
      quantityConstraints,
    };
  }

  /**
   * @returns {import('@theme/variant-picker').default | null}
   */
  #getVariantPicker() {
    const container =
      this.closest('product-card') ??
      this.closest('dialog') ??
      this.closest(
        '.shopify-section'
      );

    const pickers =
      /** @type {import('@theme/variant-picker').default[]} */
      (
        Array.from(
          container?.querySelectorAll(
            'variant-picker, swatches-variant-picker-component'
          ) ?? []
        )
      );

    const productId =
      this.dataset.productId;

    if (productId) {
      const matchingPicker =
        pickers.find(
          (picker) =>
            picker.dataset.productId ===
            productId
        );

      if (matchingPicker) {
        return matchingPicker;
      }
    }

    return pickers.length === 1
      ? pickers[0] ?? null
      : null;
  }

  /**
   * @returns {boolean}
   */
  #isAddToCartDisabled() {
    const containers =
      /** @type {NodeListOf<AddToCartComponent>} */
      (
        this.querySelectorAll(
          'add-to-cart-component'
        )
      );

    return Array.from(
      containers
    ).some(
      (container) =>
        container.refs
          .addToCartButton
          ?.disabled
    );
  }
}

if (
  !customElements.get(
    'product-form-component'
  )
) {
  customElements.define(
    'product-form-component',
    ProductFormComponent
  );
}